import { useState } from "react";
import { Link } from "react-router-dom";
import PlusMinusInput from "components/Form/PlusMinusInput/PlusMinusInput";
import { currencyFormat } from "helpers/functions";
import useAPI from "hooks/useAPI";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import scss from "./MobileCheckout.module.scss";
import { useTranslation } from "react-i18next";
import { isPriceVisible, isPurchaseAllowed } from "../../../helpers/product";

const CheckoutItem = ({ item, showImage = true, translateKey }) => {
    const { t } = useTranslation();

    const api = useAPI();
    const [openTooltip, setOpenTooltip] = useState(false);

    // Price display or purchase could be disabled for product after it was added to cart
    const price_visible = isPriceVisible(item?.product?.behaviours?.checkout);
    const can_purchase = isPurchaseAllowed(item?.product?.behaviours?.checkout);
    const formatPrice = (value, currency) => (price_visible ? currencyFormat(value, currency) : t("global-default-text-price_on_request", "Cena na upit"));

    // Track the quantity to add to the cart
    const setQuantity = (quantity) => {
        api.globalAddToCart(item?.product?.id, quantity, loading, setLoading, null, null, null, "replace");
    };

    const rabateCalc = (item) => {
        let rabate = [];
        item?.product?.price?.per_item?.rebate?.active &&
            item?.product?.price?.per_item?.rebate?.rebates?.map((rebate) => {
                rabate.push(rebate?.calc?.calc_name);
                return null;
            });
        return rabate.join("; ");
    };

    const [loading, setLoading] = useState(false);

    return (
        <div className={scss.product}>
            <div className={scss.productHeader}>
                <Link to={`/katalog/artikal/${item?.product?.id}`} className={scss.productName}>
                    {item?.product?.basic_data?.name}
                    {item?.product?.basic_data?.attributes_text && <span className={scss.attributes}>{item.product.basic_data.attributes_text}</span>}
                </Link>
                <button
                    type="button"
                    className={scss.productRemove}
                    aria-label={`Izbriši ${item?.product?.basic_data?.name}`}
                    title="Izbriši"
                    disabled={loading}
                    onClick={() => api.globalAddToCart(item?.product?.id, 0, loading, setLoading)}
                >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M4.5 7h15M9.5 7V4.5h5V7M6.5 7l.7 12a1.5 1.5 0 0 0 1.5 1.4h6.6a1.5 1.5 0 0 0 1.5-1.4l.7-12M10 10.5v6M14 10.5v6" />
                    </svg>
                </button>
            </div>
            {!can_purchase && <div className={scss.unavailable}>{t("cart_checkout-default-text-product_purchase_not_allowed", "Artikal trenutno nije moguće kupiti.")}</div>}
            <div className={scss.productDetails}>
                <div className={scss.detailRow}>
                    <span>{t(`${translateKey}-code`)}:</span>
                    <strong>{item?.product?.basic_data?.sku}</strong>
                </div>
                <div className={scss.detailRow}>
                    <span>{t(`${translateKey}-base_price`)}:</span>
                    <strong className={scss.money}>{formatPrice(item?.product?.price?.per_item?.price_with_out_vat, item?.product?.price?.currency)}</strong>
                </div>
                <div className={scss.detailRow}>
                    <span>{t(`${translateKey}-rebate`)}:</span>
                    <strong>{item?.product?.price?.per_item?.rebate?.active ? rabateCalc(item) : "0"}</strong>
                </div>
                <div className={scss.detailRow}>
                    <span>{t(`${translateKey}-discount`)}:</span>
                    <strong className={scss.money}>{item?.product?.price?.per_item?.discount?.active ? "-" + currencyFormat(item?.product?.price?.per_item?.discount?.amount, item?.product?.price?.currency) : "0"}</strong>
                </div>
                <div className={scss.detailRow}>
                    <span>{t(`${translateKey}-price_excluding_vat`)}:</span>
                    <Tooltip
                        open={openTooltip}
                        onClick={() => {
                            setOpenTooltip(true);
                        }}
                        onClose={() => {
                            setOpenTooltip(false);
                        }}
                        placement={"top"}
                        arrow={true}
                        title={
                            price_visible && (
                                <Box>
                                    <Box>
                                    {t(`${translateKey}-regular_price`)}: <span>{currencyFormat(item?.product?.price?.per_item?.price_with_out_vat)}</span>
                                    </Box>
                                    {item?.product?.price?.per_item?.rebate?.active &&
                                        item?.product?.price?.per_item?.rebate?.rebates?.map((rebate) => {
                                            return (
                                                <Box key={item?.product?.id}>
                                                    {t(`${translateKey}-price_with_rebate`)} ({rebate?.calc?.calc_name}): <span>{currencyFormat(rebate?.calc?.price)}</span>
                                                </Box>
                                            );
                                        })}
                                    {item?.product?.price?.per_item?.discount?.active &&
                                        item?.product?.price?.per_item?.discount?.campaigns?.map((campaign) => {
                                            return (
                                                <Box key={item?.product?.id}>
                                                    {t(`${translateKey}-discounted_price`)} ({campaign?.calc?.calc_name}): <span>{currencyFormat(campaign?.calc?.price)}</span>
                                                </Box>
                                            );
                                        })}
                                </Box>
                            )
                        }
                    >
                        <strong
                            className={`${scss.money} ${scss.tcPrice} ${item?.product?.price?.per_item?.discount?.active ? scss.discounted : ""} ${
                                item?.product?.price?.per_item?.rebate?.active ? scss.rebateStyle : ""
                            }  ${item?.product?.price?.per_item?.discount?.active && item?.product?.price?.per_item?.rebate?.active ? scss.discountRabate : ""}`}
                        >
                            {formatPrice(item?.product?.price?.per_item?.price_subtotal, item?.product?.price?.currency)}
                        </strong>
                    </Tooltip>
                </div>
            </div>
            <div className={scss.productBottom}>
                <div className={scss.quantityGroup}>
                    <span>{t(`${translateKey}-quantity`)}:</span>
                    {can_purchase ? (
                        <PlusMinusInput
                            behaviours={item?.product?.behaviours?.checkout}
                            max={Number(item?.product?.inventory?.amount ?? 0)}
                            quantity={Number(item?.cart?.quantity ?? 0)}
                            setQuantity={setQuantity}
                            debounceId={item?.product?.id + "_responsive_mob"}
                            className={scss.productQuantity}
                        />
                    ) : (
                        <strong>{Number(item?.cart?.quantity ?? 0)}</strong>
                    )}
                </div>
                <div className={scss.totalGroup}>
                    <span>{t(`${translateKey}-total_excluding_vat`)}:</span>
                    <strong>{formatPrice(item?.product?.price?.cost?.subtotal, item?.product?.price?.currency)}</strong>
                </div>
            </div>
        </div>
    );
};

export default CheckoutItem;
